import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Badge,
  Menu,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

import {
  useGetNotificationsPaginatedQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
} from "../../apis/NotificationApi";
import { getUserData } from "../../auth";

const NotificationDropdown = ({ mobile = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const userId = getUserData()?.uid || "";

  const {
    data,
    isLoading,
    isError,
  } = useGetNotificationsPaginatedQuery(
    { userId, page: 0, size: 10 },
    { skip: !userId }
  );

  const notifications = data?.content || [];

  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [markAllNotificationsAsRead] = useMarkAllNotificationsAsReadMutation();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationAsRead(id).unwrap();
      handleClose();
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(userId).unwrap();
      handleClose();
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <IconButton
        onClick={handleNotificationClick}
        sx={{ color: "#ffffff" }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon fontSize="medium" />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            width: "350px",
            maxHeight: "500px",
            padding: 0,
            marginTop: mobile ? 0 : "10px",
          },
        }}
      >
        <Box
          sx={{
            padding: "10px 15px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Notifications</Typography>
          <button
            onClick={handleMarkAllAsRead}
            style={{
              background: "none",
              border: "none",
              color: "#1976d2",
              cursor: "pointer",
              fontSize: "0.875rem",
            }}
          >
            Mark all as read
          </button>
        </Box>
        <Divider />

        <List sx={{ padding: 0 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : isError ? (
            <ListItem>
              <ListItemText primary="Error loading notifications." />
            </ListItem>
          ) : notifications.length > 0 ? (
            notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  backgroundColor: notification.read ? "inherit" : "#f5f5f5",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleMarkAsRead(notification.id);
                }}
              >
                <ListItemText
                  primary={notification.title}
                  secondary={
                    <>
                      <Typography variant="body2" color="textSecondary">
                        {notification.message}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", marginTop: "4px" }}
                      >
                        {new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  }
                  primaryTypographyProps={{
                    fontWeight: notification.read ? "normal" : "bold",
                  }}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary="No new notifications" />
            </ListItem>
          )}
        </List>

        <Divider />
        <Box sx={{ padding: "10px 15px", textAlign: "center" }}>
          <NavLink
            to="/notifications"
            style={{ color: "#1976d2", textDecoration: "none" }}
            onClick={handleClose}
          >
            View All Notifications
          </NavLink>
        </Box>
      </Menu>
    </Box>
  );
};

export default NotificationDropdown;
